export const INSIGHTS = [
  {
    slug: "why-training-budgets-fail",
    title: "Why Training Budgets Fail: The Difference Between Learning and Capability",
    excerpt: "Organisations spend seriously on training and see little change. The problem is rarely the content — it is the confusion of learning with capability.",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "The attendance illusion",
        paragraphs: [
          "Every year, organisations invest significant sums in training their people, and every year many of those same organisations conclude — quietly, in review meetings — that not much changed. Completion rates were high. Feedback scores were positive. The behaviour of the business did not move.",
          "This is the attendance illusion: the belief that exposure to content produces capability. It does not. It produces exposure to content. The distance between the two is where most training budgets go to die.",
        ],
      },
      {
        heading: "What capability actually means",
        paragraphs: [
          "Capability is not what a person knows; it is what they reliably do, under real conditions, when the situation is ambiguous and the stakes are real. A manager who can describe good delegation but cannot delegate under pressure does not have the capability of delegation. They have the vocabulary of it.",
          "The distinction matters because it changes how development must be designed. Knowledge transfers in a classroom. Capability transfers only through application — structured attempts, feedback, correction and repetition against real work.",
        ],
      },
      {
        heading: "The transfer problem",
        paragraphs: [
          "Learning science has known for decades that the default outcome of training is non-transfer: people leave the room and return to a system — incentives, workload, habits, a manager — that pulls them back to old behaviour within weeks. Designing a programme without designing for transfer is designing for applause.",
          "Transfer requires three things: the learner works on their own live situations, not case studies about strangers; the organisation reinforces the new behaviour; and someone measures what changed afterwards.",
        ],
      },
      {
        heading: "What works instead",
        paragraphs: [
          "Programmes that build capability share a pattern. They are anchored in frameworks that structure thinking rather than content that fills it. They run long enough for repetition. They demand application between sessions, on the learner's real business. And they define success as an observable change in what the person or the business can do.",
          "Before approving the next training budget, ask one question: what will people be able to do afterwards that they cannot do now — and how will we know? If the answer is vague, the investment is too.",
        ],
      },
    ],
    related: { label: "Explore The Manager Effectiveness Journey", to: "/programmes#manager-effectiveness-journey" },
  },
  {
    slug: "strategy-is-a-quality-of-thinking",
    title: "Strategy Is a Quality of Thinking Before It Is a Document",
    excerpt: "Most organisations have a strategy document. Far fewer have strategy. The difference is not in the slides — it is in the quality of thinking that preceded them.",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "The planning trap",
        paragraphs: [
          "Every planning season, leadership teams produce documents full of initiatives, targets and timelines — and call it strategy. But a plan is a list of actions. A strategy is a set of choices: where we will play, how we will win, and — hardest of all — what we will deliberately not do.",
          "The planning trap is seductive because planning feels productive. It fills calendars and spreadsheets. Strategy, by contrast, requires the discomfort of choosing, and choosing means accepting the cost of the paths not taken.",
        ],
      },
      {
        heading: "Thinking before planning",
        paragraphs: [
          "Genuine strategy begins upstream of the plan, in the quality of the thinking: how honestly the leadership sees its current position, how clearly it understands where it actually wins and why, and how rigorously it tests its assumptions about customers, competitors and its own capabilities.",
          "This is why two organisations can attend the same planning workshop and produce wildly different outcomes. The template is identical. The thinking is not.",
        ],
      },
      {
        heading: "Three questions that reveal strategic quality",
        paragraphs: [
          "First: can every member of the leadership team state the strategy in one sentence — the same sentence? If five executives give five answers, there is no strategy, only five private ones.",
          "Second: what has the organisation explicitly decided not to do? A strategy without sacrifice is a wish list. Third: do resource allocation and the executive calendar match the stated choices? The real strategy of any organisation is written in its budget and its leaders' diaries, not in its documents.",
        ],
      },
      {
        heading: "Building the discipline",
        paragraphs: [
          "Strategic thinking is trainable. It improves with frameworks that force clarity, with honest external challenge, and with the habit of reviewing decisions against their original reasoning. Organisations that treat strategy as an annual event get an annual document. Organisations that treat it as a continuous discipline get something rarer: direction that survives contact with reality.",
        ],
      },
    ],
    related: { label: "Explore Strategic Thinking for Senior Leaders", to: "/programmes#strategic-thinking-for-senior-leaders" },
  },
  {
    slug: "the-executives-ai-question",
    title: "The Executive's AI Question Is Not 'What Can It Do?'",
    excerpt: "Executives do not need to become technologists. They need the fluency to ask the one question that matters: what should this change about how we compete, decide and work?",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "The wrong question",
        paragraphs: [
          "Most executive conversations about AI begin with capability: what the tools can do, how fast they are improving, what the demonstrations show. This is the wrong starting point. Technology capability is abundant and accelerating; organisational advantage comes from somewhere else entirely.",
          "The leaders who compound value from AI are not those who best understand the models. They are those who best understand their own business — clearly enough to see where intelligent automation changes the economics, the customer experience or the speed of decision.",
        ],
      },
      {
        heading: "Fluency without code",
        paragraphs: [
          "Executive AI fluency is a judgement skill, not a technical one. It means knowing what AI is genuinely good at — pattern, prediction, generation, summarisation at scale — and what it is not: accountability, taste, context and the responsibility for consequences. It means being able to interrogate a vendor's claim, size a realistic return, and spot where the risk actually sits.",
          "An executive does not need to build the engine. They need to know where to drive, and what a competent driver looks like when hiring one.",
        ],
      },
      {
        heading: "Where it actually changes the executive's job",
        paragraphs: [
          "AI compresses the cost of analysis, drafting and coordination — which quietly reprices the value of judgement. When anyone can generate ten options in a minute, the scarce skill becomes choosing well among them. Critical thinking, decision quality and clear strategic intent do not become less important in an AI-rich organisation; they become the bottleneck.",
          "This is the uncomfortable and liberating truth: AI raises the premium on exactly the capabilities executive education exists to build.",
        ],
      },
      {
        heading: "A disciplined starting point",
        paragraphs: [
          "Start not with a transformation programme but with a question audit: where does the business spend the most on analysis, drafting and coordination? What would we do differently if those were nearly free? Which decisions would improve if information arrived faster and cheaper? Answer those honestly and the first investments select themselves — small, testable and pointed at real economics rather than theatre.",
        ],
      },
    ],
    related: { label: "Explore Digital & AI Fluency for Executives", to: "/programmes#digital-and-ai-fluency-for-executives" },
  },
  {
    slug: "the-manager-is-the-strategy",
    title: "The Manager Is the Strategy",
    excerpt: "Strategies do not fail in the boardroom; they fail in the middle. The quality of an organisation's managers quietly determines the quality of its execution.",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "Where strategy actually lives",
        paragraphs: [
          "A strategy is announced at the top and judged at the bottom, but it is executed in the middle. Between the leadership's intent and the customer's experience stands a layer of managers translating priorities into daily work — or failing to.",
          "Organisations routinely underinvest here. Senior leaders get development programmes; frontline staff get training; managers get promoted on Friday and left alone on Monday. Then everyone wonders why the strategy looks better in the deck than in the numbers.",
        ],
      },
      {
        heading: "The multiplication effect",
        paragraphs: [
          "A manager is a multiplier. Their clarity becomes their team's priorities; their standards become its quality bar; their coaching becomes its growth rate. Improve one manager and you improve the output of everyone they lead — which is why manager effectiveness is among the highest-leverage investments an organisation can make.",
          "The reverse is equally true. A manager who avoids difficult conversations, sets vague expectations or hoards decisions does not underperform alone — they tax everyone around them, quietly and continuously.",
        ],
      },
      {
        heading: "Management is a discipline, not a promotion",
        paragraphs: [
          "The persistent myth is that good individual contributors naturally become good managers. They do not. Management is a separate craft with its own disciplines: setting clarity, coaching performance, creating accountability and building an environment where people can do their best work.",
          "None of these is mysterious. All of them are learnable. What they require is exactly what craft always requires: frameworks, deliberate practice and honest feedback.",
        ],
      },
      {
        heading: "What effective organisations do",
        paragraphs: [
          "Organisations that execute well treat managers as a strategic population. They define what good management looks like in their context, develop it deliberately, and measure it. They understand that engagement scores, retention and delivery reliability are, to a remarkable degree, reports on management quality.",
          "If your strategy is sound and your results are not, look at the middle. The gap between the two is usually a manager who was never given the capability the strategy assumed.",
        ],
      },
    ],
    related: { label: "Explore The Manager Effectiveness Journey", to: "/programmes#manager-effectiveness-journey" },
  },
  {
    slug: "pricing-is-a-story-about-value",
    title: "Pricing Is a Story About Value",
    excerpt: "Price is not a number on a list; it is a claim about value. Organisations that cannot articulate their value end up discounting it.",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "The discount reflex",
        paragraphs: [
          "When a deal stalls, the first lever most organisations reach for is price. It is fast, it is easy to approve, and it feels like action. It is also, most of the time, an admission: that neither the seller nor the buyer can clearly articulate why the offer is worth what it costs.",
          "Discounting rarely wins the argument about value. It ends the argument about value — by conceding it.",
        ],
      },
      {
        heading: "Value is defined by the customer",
        paragraphs: [
          "Value is not a property of your product; it is a property of the customer's situation. The same offer is worth very different amounts to different customers, at different moments, against different alternatives. Pricing well therefore begins not in finance but in customer thinking: understanding what outcome the customer is actually buying and what that outcome is worth to them.",
          "A firm that sells certainty can price for certainty. A firm that sells features can only price against the next feature list — and someone always has a cheaper one.",
        ],
      },
      {
        heading: "Price sends a signal",
        paragraphs: [
          "Customers read price as information. A premium price, backed by a credible story, signals confidence and quality. A hastily discounted price signals that the first number was not real — and invites the negotiation to continue downward.",
          "This is why pricing discipline is a strategic act. Every discount teaches the market how to treat you. Every held price, justified by articulated value, teaches it something too.",
        ],
      },
      {
        heading: "Building commercial confidence",
        paragraphs: [
          "The antidote to the discount reflex is not stubbornness; it is fluency. Salespeople and leaders who can trace the customer's economics, quantify the outcome they enable and present it in the customer's language rarely need to lead with price.",
          "Commercial thinking is trainable. And few capabilities pay for themselves as quickly — the difference between a discounted and a defended price falls straight to margin.",
        ],
      },
    ],
    related: { label: "Explore the Sales & Commercial Excellence Programme", to: "/programmes#sales-and-commercial-excellence-programme" },
  },
  {
    slug: "culture-is-what-you-reward",
    title: "Culture Is What You Reward",
    excerpt: "Culture is not what the values poster says. It is what the incentive system pays for, what leaders tolerate and what gets people promoted.",
    minutes: 5,
    date: "July 2026",
    sections: [
      {
        heading: "The poster and the pay slip",
        paragraphs: [
          "Almost every organisation has a statement of values. Far fewer have examined whether their systems agree with it. The poster says collaboration; the bonus plan rewards individual heroics. The poster says customer first; the metrics reward internal speed. The poster says integrity; the top performer who cuts corners gets promoted.",
          "People are not fooled by the gap. They read the real culture from the incentive system, and they behave accordingly.",
        ],
      },
      {
        heading: "Culture as a system",
        paragraphs: [
          "Thinking of culture as a system changes the leadership task. Culture stops being a communications exercise — better posters, better town halls — and becomes a design exercise: what do we measure, what do we reward, what do we tolerate, whom do we promote?",
          "Each of these is a lever with a predictable effect. An organisation that rewards quarterly revenue will get quarterly behaviour. One that rewards customer retention will get customer attention. The system never lies about what it values, even when the leadership does.",
        ],
      },
      {
        heading: "The leadership shadow",
        paragraphs: [
          "Above every system stands a simpler force: what leaders visibly do. Teams watch what gets praised, what gets ignored and what gets excused at the top, and they calibrate. A leader who celebrates a win achieved badly has just trained the organisation in how to win.",
          "Culture change therefore starts uncomfortably close to home — with the behaviour leaders model and the behaviour they are willing to confront.",
        ],
      },
      {
        heading: "A practical audit",
        paragraphs: [
          "A useful exercise: take your stated values and, for each, find the mechanism that reinforces it. If 'customer first' is a value, where does it appear in targets, reviews and promotion decisions? If the honest answer is nowhere, the value is decorative.",
          "The encouraging news is that culture, understood this way, is designable. Change what you reward, and the culture follows — slowly, then suddenly.",
        ],
      },
    ],
    related: { label: "Explore Building the Customer-Centric Organisation", to: "/programmes#building-the-customer-centric-organisation" },
  },
];
